import dayjs from '~/timeInit';

import type { CppInvoiceTransaction } from '~/services/LuxLoyalty';

/** The minimum a row needs to expose for its cost status to be worked out. */
type CostBearing = Pick<
  CppInvoiceTransaction,
  'amountCents' | 'unpriced' | 'exclusionReason' | 'exclusionDetail' | 'costSource' | 'calculatedAt'
>;

/**
 * Why a row carries no cost.
 *
 * `EXCLUDED` and `UNPRICED` both mean no money, and the service sets `unpriced` for both, but they
 * are opposites in intent: an exclusion is the invoice deliberately declining to bill for
 * something, while an unpriced row is one nothing *could* price — that is a data problem and it
 * blocks the invoice. Rendering them identically tells an admin to go fix something that is
 * working as designed.
 */
export type CostStatusKind = 'PRICED' | 'EXCLUDED' | 'UNPRICED';

/** How a row's cost (or absence of one) should be presented. */
export interface CostStatus {
  kind: CostStatusKind;
  /** Chip severity; undefined for a normally priced row. */
  severity?: 'info' | 'error';
  label?: string;
  tooltip?: string;
}

/** Fallback prose per exclusion reason, when the service sends no `exclusionDetail`. */
const EXCLUSION_FALLBACK: Record<NonNullable<CppInvoiceTransaction['exclusionReason']>, string> = {
  REFUND_ON_TIERED_CARD: 'This refund reverses activity priced on a tiered rate card and is not billed.',
};

/**
 * Works out how a transaction's cost should be presented.
 *
 * `exclusionReason` is checked before `unpriced` because it is the more specific answer: an
 * excluded row always implies `unpriced`, but not the other way round.
 */
export function resolveCostStatus(transaction: CostBearing): CostStatus {
  if (transaction.exclusionReason) {
    return {
      kind: 'EXCLUDED',
      severity: 'info',
      label: 'Excluded',
      tooltip: transaction.exclusionDetail ?? EXCLUSION_FALLBACK[transaction.exclusionReason],
    };
  }

  if (transaction.amountCents === null || transaction.unpriced) {
    return {
      kind: 'UNPRICED',
      severity: 'error',
      label: 'Unpriced',
      tooltip:
        'Nothing could price this row — no rate card covered the day it is priced against. This blocks the invoice.',
    };
  }

  return { kind: 'PRICED' };
}

/**
 * Describes where the costs on the current page came from.
 *
 * Deliberately scoped to the page, in wording as well as in fact. The grid is paginated server
 * side and `costSource` is per row, so a period split across pages can genuinely report a
 * different source on each — a page-derived claim phrased as a verdict on the whole period would
 * be one an admin could reasonably act on when deciding whether to force refresh.
 *
 * A page counts as served from storage only when every row on it is. `CALCULATED` does not imply
 * the numbers are wrong — an uncached period computes identical figures — so the copy avoids
 * suggesting staleness and instead gives the calculation date.
 *
 * @param transactions - The rows on the current page.
 * @returns Display copy, or null when there are no rows to describe.
 */
export function describeCostSource(transactions: Array<CostBearing>): string | null {
  if (transactions.length === 0) {
    return null;
  }

  const isFullyPersisted = transactions.every((transaction) => transaction.costSource === 'PERSISTED');

  if (!isFullyPersisted) {
    return 'Costs on this page were computed for this view.';
  }

  const timestamps = transactions
    .map((transaction) => transaction.calculatedAt)
    .filter((value): value is string => value !== null);

  if (timestamps.length === 0) {
    return 'Costs on this page came from a stored calculation.';
  }

  const latest = timestamps.reduce((newest, value) => (value > newest ? value : newest));
  return `Costs on this page came from a stored calculation of ${dayjs(latest).format('DD MMM YYYY, HH:mm')}.`;
}

/** How many rows on a page carry no cost, split by why. */
export interface CostStatusTally {
  /** Rows nothing could price. A data problem, and a blocker on the invoice. */
  unpriced: number;
  /** Rows the invoice deliberately declines to bill. Working as designed. */
  excluded: number;
}

/**
 * Counts the rows on a page that carry no cost.
 *
 * Drives the warning above the grid. Scoped to the rows actually on screen rather than the period:
 * the period-level story is already told by the invoice's own blockers, and conflating the two
 * would have the banner claim rows an admin cannot see on this page.
 *
 * @param transactions - The rows on the current page.
 */
export function tallyCostStatuses(transactions: Array<CostBearing>): CostStatusTally {
  return transactions.reduce<CostStatusTally>(
    (tally, transaction) => {
      const { kind } = resolveCostStatus(transaction);

      if (kind === 'UNPRICED') {
        return { ...tally, unpriced: tally.unpriced + 1 };
      }
      if (kind === 'EXCLUDED') {
        return { ...tally, excluded: tally.excluded + 1 };
      }
      return tally;
    },
    { unpriced: 0, excluded: 0 },
  );
}
